<script setup lang="ts">
const $fetch = (globalThis as any).$fetch as <T = any>(url: string, opts?: any) => Promise<T>

const route = useRoute()
const { locale } = useI18n()

const runtimeConfig = useRuntimeConfig()

const previewSecret = runtimeConfig?.previewSecret || runtimeConfig?.public?.previewSecret
const isPreview = computed(() => route?.query?.previewSecret === previewSecret)

const { data, error } = await useAsyncData(
  () => `person-page-${route.params.slug}-${locale.value}`,
  () =>
    $fetch('/api/payload-gql', {
      method: 'POST',
      body: {
        query: SinglePersonQuery,
        variables: {
          slug: { equals: route.params.slug },
          locale: locale.value,
          isPreview: isPreview.value,
          // with `draft: true` payload returns each doc's latest version, whatever its
          // status -- so preview must accept both, or a doc with no pending draft
          // (latest version published) matches nothing and the page renders empty
          statuses: isPreview.value ? ['draft', 'published'] : ['published'],
        },
        isPreview: isPreview.value,
      },
    },
  ),
  { transform: (data: any) => transformPayloadData<any>(data) as Page }
)

if (error.value) {
  throw createError({ statusCode: 500, statusMessage: 'Error fetching page' })
}

if (!data.value && !isPreview.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found' })
}

// people without content or relations are not linked to from the list, so they
// have no page to land on either -- `hasDetailPage` is derived on save in
// payload (`utilities/derived-flags.ts`) and is the same flag search indexes on
if (data.value && !isPreview.value && !data.value.hasDetailPage) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found' })
}

usePageSeo({
  title: () => data.value?.title,
  description: () => data.value?.description,
  image: () => data.value?.images?.[0],
  type: 'article',
})
</script>

<template>
  <!-- opens on top of the people list, which stays mounted underneath;
       page-modal owns the scroll lock, the escape key and the fade -->
  <page-modal :parent-route="{ name: 'people' }">
    <pages-people-slug-panel
      v-if="data"
      :data="data"
    >
      <template #close>
        <site-link
          :route-object="{ name: 'people' }"
          class="group icon-box-cross"
          :aria-label="$t('labels.close')"
        >
          <svgo-cross class="w-full overflow-visible stroke-black stroke-default group-hover:stroke-accent icon-hover" />
        </site-link>
      </template>
    </pages-people-slug-panel>
  </page-modal>
</template>
